import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { fetchNotificationStats, fetchSilencedAlarmTypes } from '../services/notifications/notificationService';
import { StateContext } from './StateContext';
import { PortalClientStorage } from '../services/client-storage/storage';
import { isDevPortalEnvironment } from '../utils/deploymentEnvironment';

export const POLL_INTERVAL_MS = 10_000;
export const NotificationsContext = createContext({
    stats: null, activeCount: 0, activeCountVersion: 0, callerKey: '',
    silencedAlarmTypes: null, preferencesError: false, silencingUnavailable: false,
    requestsEnabled: true,
    refreshStats: () => {}, refresh: async () => {},
});

const getHasNotificationsRole = () => {
    try {
        const roles = JSON.parse(PortalClientStorage.getData('currentUser', true));
        return Array.isArray(roles) && (roles.includes('YukonSupport') || roles.includes('OTADLMAdmin'));
    } catch {
        return false;
    }
};

export const NotificationsProvider = ({ children }) => {
    const { setNotifBadgeCount, isAuthenticated, email, accessToken } = useContext(StateContext) || {};
    const callerKey = JSON.stringify([isAuthenticated, email || accessToken || '']);
    const allowed = isAuthenticated !== false && isDevPortalEnvironment() && getHasNotificationsRole();
    const [snapshot, setSnapshot] = useState(null);
    const [preferences, setPreferences] = useState(null);
    const [preferencesError, setPreferencesError] = useState(false);
    const [activeCountVersion, setActiveCountVersion] = useState(0);
    const currentCaller = useRef(callerKey);
    currentCaller.current = callerKey;
    const statsRequest = useRef(0);
    const preferenceRequest = useRef(0);
    const activeCountRef = useRef(0);
    const mounted = useRef(false);

    const loadStats = useCallback(async () => {
        const request = ++statsRequest.current;
        if (!allowed) return;
        try {
            const next = await fetchNotificationStats({ notificationType: 'alarm' });
            if (!mounted.current || currentCaller.current !== callerKey || request !== statsRequest.current) return;
            setSnapshot({ callerKey, stats: next });
            const count = (next?.bySeverity?.critical || 0) + (next?.bySeverity?.warning || 0) + (next?.bySeverity?.info || 0);
            if (count !== activeCountRef.current) {
                activeCountRef.current = count;
                setActiveCountVersion((v) => v + 1);
            }
            setNotifBadgeCount?.((next?.bySeverity?.critical || 0) + (next?.bySeverity?.warning || 0));
        } catch {
            // Stats polling is non-blocking; preference failures remain explicit below.
        }
    }, [allowed, callerKey, setNotifBadgeCount]);

    const loadPreferences = useCallback(async () => {
        const request = ++preferenceRequest.current;
        if (!allowed) return;
        try {
            const response = await fetchSilencedAlarmTypes();
            if (!mounted.current || currentCaller.current !== callerKey || request !== preferenceRequest.current) return;
            setPreferences({ callerKey, types: response.items.map((item) => item.alarmType) });
            setPreferencesError(false);
        } catch {
            if (!mounted.current || currentCaller.current !== callerKey || request !== preferenceRequest.current) return;
            setPreferences(null);
            setPreferencesError(true);
        }
    }, [allowed, callerKey]);

    const refresh = useCallback(async () => {
        if (currentCaller.current !== callerKey || !mounted.current) return;
        // This is an invalidation signal, not merely a count-change signal.
        setActiveCountVersion((v) => v + 1);
        await Promise.all([loadStats(), loadPreferences()]);
    }, [callerKey, loadStats, loadPreferences]);

    useEffect(() => {
        mounted.current = true;
        activeCountRef.current = 0;
        setSnapshot(null);
        setPreferences(null);
        setPreferencesError(false);
        setNotifBadgeCount?.(0);
        loadStats();
        loadPreferences();
        const timer = setInterval(() => {
            if (!document.hidden) loadStats();
        }, POLL_INTERVAL_MS);
        const onVisibility = () => { if (!document.hidden) loadStats(); };
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            mounted.current = false;
            ++statsRequest.current;
            ++preferenceRequest.current;
            clearInterval(timer);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [loadStats, loadPreferences, setNotifBadgeCount]);

    const stats = allowed && snapshot?.callerKey === callerKey ? snapshot.stats : null;
    const activeCount = (stats?.bySeverity?.critical || 0) + (stats?.bySeverity?.warning || 0) + (stats?.bySeverity?.info || 0);
    return (
        <NotificationsContext.Provider value={{
            stats, activeCount, activeCountVersion, callerKey,
            requestsEnabled: allowed,
            silencedAlarmTypes: allowed && preferences?.callerKey === callerKey ? preferences.types : null,
            preferencesError, silencingUnavailable: Boolean(stats?.silencingUnavailable),
            refreshStats: loadStats, refresh,
        }}>
            {children}
        </NotificationsContext.Provider>
    );
};

export const useNotificationsContext = () => useContext(NotificationsContext);
